import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import {Popover} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useState} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {
    applyScopeSelectionToFilterConfiguration,
    getScopeFilterDrawerTitle,
    getScopeFilterSelectionMode,
    getSelectedScopeOptionIdsFromFilter,
    isScopeFilterAllState,
} from "~/components/ottopilotDataViewer/scopeFilterConfiguration";
import type {ScopeFilterConfig, ScopeFilterResolutionContext} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {AnankeBadge} from "@vesta/ananke/components/anankeBadge";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeCheckbox} from "@vesta/ananke/components/anankeCheckbox";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

function scopeTriggerLabel<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
    config: ScopeFilterConfig,
    resolutionContext: ScopeFilterResolutionContext,
): string {
    if (isScopeFilterAllState(controller.filterConfiguration, config) === true) {
        return "All";
    }
    const selected = getSelectedScopeOptionIdsFromFilter(controller.filterConfiguration, config, resolutionContext);
    const mode = getScopeFilterSelectionMode(config);
    if (mode === "single") {
        const firstId = [...selected][0];
        if (firstId === undefined) {
            return "All";
        }
        const option = config.options.find((opt) => opt.id === firstId);
        return option !== undefined ? option.label : "All";
    }
    if (selected.size === 0) {
        return "All";
    }
    if (selected.size === 1) {
        const onlyId = [...selected][0];
        const option = config.options.find((opt) => opt.id === onlyId);
        return option !== undefined ? option.label : "All";
    }
    return "Selected";
}

export function ScopeFilterComponent<T extends {id: Uuid}>({
    controller,
    scopeFilterConfig,
}: {
    controller: OttopilotDataViewerController<T>;
    scopeFilterConfig: ScopeFilterConfig;
}) {
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const [opened, setOpened] = useState(false);
    const [ephemeralSelectedIds, setEphemeralSelectedIds] = useState<Set<Uuid>>(new Set());
    const {workspaceUser} = useWorkspaceUser();
    const resolutionContext: ScopeFilterResolutionContext = {
        workspaceUserId: workspaceUser.userId,
    };

    const mode = getScopeFilterSelectionMode(scopeFilterConfig);
    const drawerTitle = getScopeFilterDrawerTitle(scopeFilterConfig);

    const openPanel = () => {
        setEphemeralSelectedIds(
            getSelectedScopeOptionIdsFromFilter(controller.filterConfiguration, scopeFilterConfig, resolutionContext),
        );
        setOpened(true);
    };

    const closePanel = () => {
        setOpened(false);
    };

    const discardAndClose = () => {
        setOpened(false);
    };

    const applyAndClose = () => {
        controller.setFilterConfiguration((prev) =>
            applyScopeSelectionToFilterConfiguration(
                prev,
                scopeFilterConfig,
                ephemeralSelectedIds.size === 0 ? null : ephemeralSelectedIds,
                resolutionContext,
            ),
        );
        setOpened(false);
    };

    const allChecked = ephemeralSelectedIds.size === 0;

    const setAllSelected = () => {
        setEphemeralSelectedIds(new Set());
    };

    const toggleOption = (optionId: Uuid) => {
        setEphemeralSelectedIds((prev) => {
            if (mode === "single") {
                if (prev.has(optionId) === true) {
                    return new Set();
                }
                return new Set([optionId]);
            }
            const next = new Set(prev);
            if (next.has(optionId) === true) {
                next.delete(optionId);
            } else {
                next.add(optionId);
            }
            return next;
        });
    };

    const triggerLabel = scopeTriggerLabel(controller, scopeFilterConfig, resolutionContext);
    const selectedScopeCount = getSelectedScopeOptionIdsFromFilter(
        controller.filterConfiguration,
        scopeFilterConfig,
        resolutionContext,
    ).size;
    const hasActiveScope = selectedScopeCount > 0 && isScopeFilterAllState(controller.filterConfiguration, scopeFilterConfig) === false;

    const triggerButton = (
        <AnankeButton
            type="button"
            appearance="default"
            spacing="default"
            isSelected={hasActiveScope}
            onClick={() => {
                if (opened === false) {
                    openPanel();
                } else {
                    closePanel();
                }
            }}
        >
            <div className="grid grid-flow-col auto-cols-max gap-x-[0.375rem] items-center">
                {triggerLabel}
                {hasActiveScope === true && mode === "multi" && selectedScopeCount > 0 ? (
                    <AnankeBadge appearance="primary">
                        {selectedScopeCount}
                    </AnankeBadge>
                ) : null}
                <ChevronDownIcon
                    label=""
                    size="small"
                />
            </div>
        </AnankeButton>
    );

    const panelBody = (
        <>
            <div className="w-full min-w-[16rem] max-w-[20rem] md:px-ds-space-100">
                <div className="w-full pr-ds-space-200 py-ds-space-100 grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-ds-space-150">
                    <AnankeCheckbox
                        isChecked={allChecked}
                        onChange={(e) => {
                            if (e.target.checked === true) {
                                setAllSelected();
                            }
                        }}
                    />
                    <div>All</div>
                </div>
                <ItemBuilder
                    items={scopeFilterConfig.options}
                    itemBuilder={(option) => (
                        <div
                            key={option.id}
                            className="w-full pr-ds-space-200 py-ds-space-075 grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-ds-space-150"
                        >
                            <AnankeCheckbox
                                isChecked={ephemeralSelectedIds.has(option.id)}
                                onChange={() => {
                                    toggleOption(option.id);
                                }}
                            />
                            <div>{option.label}</div>
                        </div>
                    )}
                />
            </div>
        </>
    );

    const panelFooter = (
        <div>
            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
            <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                <AnankeButton
                    type="button"
                    appearance="subtle"
                    onClick={discardAndClose}
                >
                    Cancel
                </AnankeButton>
                <AnankeButton
                    type="button"
                    appearance="primary"
                    onClick={applyAndClose}
                >
                    Apply
                </AnankeButton>
            </div>
        </div>
    );

    if (isMobile === true) {
        return (
            <>
                {triggerButton}
                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title={drawerTitle}
                    zIndex={100}
                    fitContentOnOpen={true}
                    footer={panelFooter}
                >
                    {panelBody}
                </BottomDrawer>
            </>
        );
    }

    return (
        <Popover
            position="bottom-start"
            width={320}
            opened={opened}
            onChange={(next) => {
                if (next === true) {
                    openPanel();
                } else {
                    closePanel();
                }
            }}
            classNames={{
                dropdown: "p-0!",
            }}
            shadow="lg"
        >
            <Popover.Target>{triggerButton}</Popover.Target>
            <Popover.Dropdown>
                {panelBody}
                {panelFooter}
            </Popover.Dropdown>
        </Popover>
    );
}
